import { API_CONFIG } from '../config/api';
import { getFirebaseAuthToken } from '../lib/firebaseAuth';

const API_BASE_URL = API_CONFIG.BASE_URL;

const getAuthToken = async (): Promise<string | null> => {
  try {
    return await getFirebaseAuthToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export const submitProfileForModeration = async (
  apiKey: string,
  profileData: {
    user_id?: string;
    images: string[];
    global_configuration?: {
      face_detect?: boolean;
      ai_image_detect?: boolean;
    };
    per_image_configuration?: Record<string, { face_detect?: boolean; ai_image_detect?: boolean }>;
    bio?: string;
    prompts?: Array<{ question?: string; answer?: string }>;
    metadata?: Record<string, any>;
  }
) => {
  const response = await fetch(`${API_BASE_URL}/api/moderation/submit-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey },
    body: JSON.stringify(profileData),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to submit profile');
  return data;
};

export const getModerationProfiles = async (filters?: {
  status?: 'approved' | 'rejected' | 'pending';
  api_key_id?: string;
  limit?: number;
  cursor?: string;
  load_all_pending?: boolean;
}) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.api_key_id) params.append('api_key_id', filters.api_key_id);
  if (filters?.limit) params.append('limit', filters.limit.toString());
  if (filters?.cursor) params.append('cursor', filters.cursor);
  if (filters?.load_all_pending) params.append('load_all_pending', 'true');

  const response = await fetch(`${API_BASE_URL}/api/moderation/profiles?${params.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch profiles');
  return data;
};

export const updateProfileStatus = async (profileId: string, status: 'approved' | 'rejected' | 'pending') => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/api/moderation/update-status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ profile_id: profileId, status }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update profile status');
  return data;
};

export const getModerationSettings = async (apiKeyId: string) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/api/moderation/settings?api_key_id=${apiKeyId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to fetch settings');
  return data;
};

export const updateModerationSettings = async (
  apiKeyId: string,
  settings: {
    auto_approve_threshold?: number;
    auto_reject_threshold?: number;
    webhook_url?: string | null;
    image_settings?: {
      global_configuration?: { face_detect: boolean; ai_image_detect: boolean };
      per_image_configuration?: Record<string, { face_detect?: boolean; ai_image_detect?: boolean }>;
      image_block?: boolean;
    } | null;
  }
) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  const requestBody: any = { api_key_id: apiKeyId };
  if (settings.auto_approve_threshold !== undefined) requestBody.auto_approve_threshold = settings.auto_approve_threshold;
  if (settings.auto_reject_threshold !== undefined) requestBody.auto_reject_threshold = settings.auto_reject_threshold;
  if (settings.webhook_url !== undefined) requestBody.webhook_url = settings.webhook_url;
  if (settings.image_settings !== undefined) requestBody.image_settings = settings.image_settings;

  const response = await fetch(`${API_BASE_URL}/api/moderation/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(requestBody),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to update settings');
  return data;
};

export const deleteProfile = async (profileId: string) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Not authenticated');
  const response = await fetch(`${API_BASE_URL}/api/moderation/profiles/${profileId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to delete profile');
  return data;
};
